import { Injectable } from '@angular/core';
import { Directionality } from '@angular/cdk/bidi';
import { UiDirection } from '../util/direction';

/**
 * The app-wide CDK `Directionality`, switchable at runtime (WI 3974).
 *
 * The CDK's root `Directionality` reads `<html dir>` once, when it is first created, and never
 * changes afterwards — so Material/CDK (overlay positions, dialogs, menus, sliders, `mat-icon`
 * button ripples, `cdkConnectedOverlay` start/end) would stay left-to-right after the user picks
 * Arabic in Settings. `app.config.ts` provides this class in its place
 * (`{ provide: Directionality, useExisting: AppDirectionality }`), and `UiLangService` calls
 * `set()` whenever the UI language changes: `value` follows and `change` emits, which is what
 * every CDK consumer subscribes to.
 */
@Injectable({ providedIn: 'root' })
export class AppDirectionality extends Directionality {
  /** Switches the direction; emits on `change` only when it actually changes. */
  set(dir: UiDirection): void {
    if (this.value === dir) return;
    // CDK 19 keeps `value` as a plain field, declared readonly for consumers; this class is its only
    // writer. Should a later CDK back it with a `valueSignal` (a getter without a setter, where the
    // assignment would throw at startup for every Arabic user), write through the signal instead —
    // ui-lang.service.spec.ts fails on the next CDK upgrade if neither holds.
    const backing = (this as unknown as { valueSignal?: { set(d: UiDirection): void } }).valueSignal;
    if (backing && typeof backing.set === 'function') backing.set(dir);
    else (this as { value: UiDirection }).value = dir;
    this.change.emit(dir);
  }
}
